import Keycloak from "keycloak-js";

export class LeanbotAuth {
  static #config = null;
  static #keycloak = null;
  static #refreshTimer = null;
  static #refreshInFlight = false;
  static #flow = "implicit";

  static setConfig(config) {
    LeanbotAuth.#config = config || null;
  }

  static isEnabled() {
    return Boolean(LeanbotAuth.#config?.Enabled);
  }

  static getAccessToken() {
    if (LeanbotAuth.#keycloak?.idToken) return LeanbotAuth.#keycloak.idToken;
    return localStorage.getItem("token") || "";
  }

  static getAuthHeaders() {
    const token = LeanbotAuth.getAccessToken();
    if (!token) return {};
    return { Authorization: `Bearer ${token}` };
  }

  static async init() {
    if (!LeanbotAuth.isEnabled()) return;

    const keycloakConfig = LeanbotAuth.#config;
    const hasRequiredConfig =
      keycloakConfig?.url &&
      keycloakConfig?.realm &&
      keycloakConfig?.clientId;

    if (!hasRequiredConfig) {
      throw new Error("Keycloak config is missing required fields: url, realm, clientId");
    }

    LeanbotAuth.#keycloak = new Keycloak({
      url: keycloakConfig.url,
      realm: keycloakConfig.realm,
      clientId: keycloakConfig.clientId,
    });

    LeanbotAuth.#flow = keycloakConfig?.flow || "implicit";
    const authenticated = await LeanbotAuth.#keycloak.init({
      onLoad: "login-required",
      flow: LeanbotAuth.#flow,
      checkLoginIframe: false,
    });

    if (!authenticated) {
      throw new Error("Keycloak authentication failed");
    }

    LeanbotAuth.#persistIdentity();
    LeanbotAuth.#startRefreshLoop();
  }

  // Leanbot toolkit bar: name + login for the shared user menu (no token leaves this class).
  static getUserProfile() {
    const p = LeanbotAuth.#keycloak?.idTokenParsed;
    if (!p) return null;
    return { name: p.name || p.preferred_username || "", login: p.preferred_username || "" };
  }

  static async logout(options) {
    if (LeanbotAuth.#refreshTimer) {
      clearTimeout(LeanbotAuth.#refreshTimer);
      LeanbotAuth.#refreshTimer = null;
    }
    // #395: clear the persisted identity BEFORE redirecting. keycloak.logout()
    // navigates away, but localStorage survives the round trip -- and `kc_token`
    // is now a live access token (realm `idp` issues 7200s ones), so leaving it
    // behind hands the next person on a shared machine a credential that any
    // offline validator still accepts. The gateway itself rejects it once the KC
    // session dies, which is the point of #395, but nothing else has to.
    LeanbotAuth.#clearPersistedIdentity();
    if (!LeanbotAuth.#keycloak) return;
    await LeanbotAuth.#keycloak.logout(options);
  }

  static #clearPersistedIdentity() {
    try {
      localStorage.removeItem("kc_token");
      localStorage.removeItem("token");
      localStorage.removeItem("username");
      delete localStorage.token;
    } catch (error) {
      console.warn("Could not clear persisted identity:", error);
    }
  }

  static #persistIdentity() {
    const username = LeanbotAuth.#keycloak?.idTokenParsed?.preferred_username || "";
    const token = LeanbotAuth.#keycloak?.idToken || "";

    // #395: the WS gateway validates via Keycloak userinfo, which requires the
    // ACCESS token (the ID token fails and the socket counts as anonymous).
    // mqtt-gateway-client reads `kc_token` first, so persist it there; the
    // legacy `token` key (ID token) stays untouched for existing consumers.
    const accessToken = LeanbotAuth.#keycloak?.token || "";
    if (accessToken) {
      localStorage.setItem("kc_token", accessToken);
    }

    localStorage.setItem("username", username);
    localStorage.setItem("token", token);
    localStorage.token = token;
  }

  static #startRefreshLoop() {
    if (LeanbotAuth.#refreshTimer) {
      clearTimeout(LeanbotAuth.#refreshTimer);
      LeanbotAuth.#refreshTimer = null;
    }

    // Keycloak implicit flow does not provide refresh token.
    // Skip updateToken() to avoid repeated runtime errors.
    if (LeanbotAuth.#flow === "implicit" || !LeanbotAuth.#keycloak?.refreshToken) {
      return;
    }

    const MIN_VALIDITY_SECONDS = 30;
    const MIN_DELAY_MS = 5000;
    const MAX_DELAY_MS = 60000;

    const scheduleNext = () => {
      if (!LeanbotAuth.#keycloak?.tokenParsed?.exp) {
        LeanbotAuth.#refreshTimer = setTimeout(refreshOnce, MIN_DELAY_MS);
        return;
      }

      const nowSec = Math.floor(Date.now() / 1000);
      const expiresInSec = LeanbotAuth.#keycloak.tokenParsed.exp - nowSec;
      const delayMs = Math.max(
        MIN_DELAY_MS,
        Math.min(MAX_DELAY_MS, (expiresInSec - MIN_VALIDITY_SECONDS) * 1000)
      );

      LeanbotAuth.#refreshTimer = setTimeout(refreshOnce, delayMs);
    };

    const refreshOnce = async () => {
      if (!LeanbotAuth.#keycloak) return;
      if (LeanbotAuth.#refreshInFlight) {
        scheduleNext();
        return;
      }

      LeanbotAuth.#refreshInFlight = true;
      try {
        const refreshed = await LeanbotAuth.#keycloak.updateToken(MIN_VALIDITY_SECONDS);
        if (refreshed) LeanbotAuth.#persistIdentity();
      } catch (error) {
        console.error("Keycloak token refresh failed:", error);
      } finally {
        LeanbotAuth.#refreshInFlight = false;
        scheduleNext();
      }
    };

    scheduleNext();
  }
}
